/**
 * Alertas operacionais entre empresas, priorizados e com UMA acao cada.
 *
 * Decisoes nao obvias:
 *  1. Gravidade tem Badge COM texto (Crítico / Atenção / Aviso), nunca so a
 *     cor do glifo. Os chips filtram por gravidade e mostram a contagem.
 *  2. A acao depende do TIPO (vocabulario.ts): integracao e WhatsApp levam
 *     ao cadastro da empresa (onde se resolve), chargeback leva as
 *     comissoes, margem negativa abre o detalhe de custos. Nao ha acao
 *     generica "ver empresa".
 *  3. "Dispensar" e estado LOCAL da previa: o alerta some da lista e uma
 *     linha de status diz que nada foi enviado ao servidor; "Restaurar" volta
 *     tudo. Nao existe rota de alertas ainda, e fingir um POST seria pior do
 *     que nao ter o botao.
 *  4. Pensado para muitos alertas: mostra `LIMITE` e um "Ver mais"; a lista
 *     inteira nunca vai para a tela sem pedir.
 */

import { useState } from 'react';
import { Badge, Button, CompanyLogo, FilterChips, Icon, LinkButton, SectionCard, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { AlertSeverity, PlatformAlert } from '../types';
import { ALERT_KIND_ACTION, ALERT_KIND_ICON, ALERT_KIND_LABEL, ALERT_SEVERITY_CHIP, ALERT_SEVERITY_LABEL, ALERT_SEVERITY_TONE } from '../vocabulario';
import { identidadeDaEmpresa } from './IdentidadeEmpresa';
import { cadastroDaEmpresa } from './TabelaPorEmpresa';

const LIMITE = 6;

type FiltroDeGravidade = 'todos' | AlertSeverity;

function AcaoDoAlerta({ alerta, nome, onDetalharCustos }: { readonly alerta: PlatformAlert; readonly nome: string; readonly onDetalharCustos: (companyId: string) => void }): JSX.Element {
  const acao = ALERT_KIND_ACTION[alerta.kind];
  switch (acao.kind) {
    case 'cadastro':
      return (
        <LinkButton to={cadastroDaEmpresa(alerta.companyId)} variant="secondary" size="sm" icon="empresa">
          {acao.label}
          <span className="sr-only"> de {nome}</span>
        </LinkButton>
      );
    case 'comissoes':
      return (
        <LinkButton to="/admin/comissoes" variant="secondary" size="sm" icon="comissao">
          {acao.label}
        </LinkButton>
      );
    case 'custos':
      return (
        <Button variant="secondary" size="sm" icon="moeda" onClick={() => onDetalharCustos(alerta.companyId)} aria-label={`${acao.label} de ${nome}`}>
          {acao.label}
        </Button>
      );
  }
}

export function AlertasDaPlataforma({
  alerts,
  companies,
  onDetalharCustos,
  className = '',
}: {
  readonly alerts: readonly PlatformAlert[];
  readonly companies: readonly CompanySummary[];
  readonly onDetalharCustos: (companyId: string) => void;
  readonly className?: string;
}): JSX.Element {
  const [dispensados, setDispensados] = useState<readonly string[]>([]);
  const [filtro, setFiltro] = useState<FiltroDeGravidade>('todos');
  const [expandido, setExpandido] = useState(false);

  const abertos = alerts.filter((a) => !dispensados.includes(a.id));
  const visiveis = abertos.filter((a) => filtro === 'todos' || a.severity === filtro);
  const mostrados = expandido ? visiveis : visiveis.slice(0, LIMITE);
  const escondidos = visiveis.length - mostrados.length;
  const criticos = abertos.filter((a) => a.severity === 'critical').length;

  const contar = (s: AlertSeverity): string => formatCount(abertos.filter((a) => a.severity === s).length);
  const chips: readonly ChipOption<FiltroDeGravidade>[] = [
    { value: 'todos', label: 'Todos', count: formatCount(abertos.length) },
    { value: 'critical', label: 'Críticos', count: contar('critical') },
    { value: 'warning', label: 'Atenção', count: contar('warning') },
    { value: 'info', label: 'Avisos', count: contar('info') },
  ];

  const selo =
    abertos.length === 0 ? (
      <Badge tone="good" dot>
        Tudo em ordem
      </Badge>
    ) : (
      <Badge tone={criticos > 0 ? 'critical' : 'warning'} dot>
        {formatCount(abertos.length)} {abertos.length === 1 ? 'aberto' : 'abertos'}
        {criticos > 0 ? ` · ${formatCount(criticos)} ${criticos === 1 ? 'crítico' : 'críticos'}` : ''}
      </Badge>
    );

  return (
    <SectionCard
      id="alertas"
      title="Alertas"
      icon="alerta"
      description="O que precisa de alguém da equipe agora: integração, WhatsApp, chargeback, implantação e margem. Do mais grave ao mais leve."
      aside={selo}
      className={className}
    >
      <div className="flex flex-col gap-3">
        {abertos.length > 0 ? <FilterChips label="Filtrar alertas por gravidade" options={chips} value={filtro} onChange={setFiltro} /> : null}

        {abertos.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-subtle">Nenhum alerta aberto. Todas as integrações receberam evento nas últimas 72 horas e nenhuma margem fechou no negativo.</p>
        ) : visiveis.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-subtle">Nenhum alerta com esta gravidade.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {mostrados.map((a) => {
              const identidade = identidadeDaEmpresa(a.companyId, companies);
              const titulo = ALERT_KIND_LABEL[a.kind];
              return (
                <li key={a.id} className="flex flex-col gap-3 rounded-xl border border-line bg-surface px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <span aria-hidden="true" className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${ALERT_SEVERITY_CHIP[a.severity]}`}>
                      <Icon name={ALERT_KIND_ICON[a.kind]} size="sm" />
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={ALERT_SEVERITY_TONE[a.severity]} dot>
                          {ALERT_SEVERITY_LABEL[a.severity]}
                        </Badge>
                        <p className="font-medium text-ink">{titulo}</p>
                      </div>
                      <p className="mt-1 text-sm text-ink-muted">
                        {a.detail}
                        {a.amountCents === null ? null : <span className="text-ink"> Valor: {formatBRLFromCents(a.amountCents)}.</span>}
                      </p>
                      <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-subtle">
                        <span className="flex items-center gap-1.5 text-ink-muted">
                          <CompanyLogo company={identidade} size="sm" />
                          {identidade.name}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span className="tabular-nums">{formatDateTime(a.occurredAt)}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <AcaoDoAlerta alerta={a} nome={identidade.name} onDetalharCustos={onDetalharCustos} />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="check"
                      onClick={() => setDispensados((atual) => [...atual, a.id])}
                      aria-label={`Dispensar o alerta "${titulo}" de ${identidade.name}`}
                    >
                      Dispensar
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {escondidos > 0 || (expandido && visiveis.length > LIMITE) ? (
          <div className="flex justify-center">
            <Button variant="ghost" size="sm" icon="chevron" onClick={() => setExpandido((v) => !v)} className={expandido ? '[&>svg]:rotate-180' : ''}>
              {expandido ? 'Ver menos' : `Ver os outros ${formatCount(escondidos)} ${escondidos === 1 ? 'alerta' : 'alertas'}`}
            </Button>
          </div>
        ) : null}

        {dispensados.length > 0 ? (
          <p role="status" className="flex flex-wrap items-center gap-2 text-xs text-ink-subtle">
            <Badge tone="neutral" icon="check">
              Dispensado na prévia
            </Badge>
            <span>
              {formatCount(dispensados.length)} {dispensados.length === 1 ? 'alerta dispensado' : 'alertas dispensados'} só nesta tela; nada foi enviado ao servidor.
            </span>
            <Button variant="ghost" size="sm" icon="atualizar" onClick={() => setDispensados([])}>
              Restaurar
            </Button>
          </p>
        ) : null}
      </div>
    </SectionCard>
  );
}
